import cloneDeep from 'lodash/cloneDeep'
import { v4 as uuid4 } from 'uuid'

import { IBody, IBodySettings, PageNature } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  BottomBarType,
  BuilderMenu,
  BuilderPage,
  IActionRepeaterValue,
  IActionType,
  TableAdapter,
  TableColumnListColumnValue,
  TableColumnListValue,
  TopBarType,
  VariableSelectFeatureFlag,
} from '@makyoapp/makyo-common'
import { MATERIAL_ICONS } from '@makyoapp/makyo-ui'

import {
  BuilderComponent,
  BuilderComponentGroup,
  ComponentSettingKey,
  DEFAULT_COMPONENTS_GROUPS,
} from '@/shared/builder'
import { ContainerComponents } from '@/shared/builder/components/containers.group'
import { createSlug, genIdent } from '@/shared/utils'

import { createBuilderElement, parseCompSettings } from './comp-parsers'
import { generateAutoFormFields } from './form-parsers'

export function getRandomMaterialIcon() {
  return MATERIAL_ICONS[Math.floor(Math.random() * MATERIAL_ICONS.length)]?.value ?? 'home'
}

const TAB_SEQUENTIAL_ICONS = ['star', 'favorite', 'person']

export function getSequentialTabIcon(tabIndex: number): string {
  return TAB_SEQUENTIAL_ICONS[tabIndex % TAB_SEQUENTIAL_ICONS.length]
}

// ============================== Page Generator ==============================

export function genNewPage(
  pages: BuilderPage[],
  tableId?: string,
  tableName?: string,
  title?: string,
  isList?: boolean,
  isPrivate?: boolean
): BuilderPage {
  const pageTitle = genIdent(
    pages as unknown as Record<string, unknown>[],
    'title',
    pages.length + 1,
    tableId ? `${tableName ? `${tableName} ` : ''}Detail Screen ` : `${title || 'Screen'} `
  )
  const randomSlug = Math.random().toString(36).substring(2, 8)
  const pageSlug = createSlug(pageTitle, `page-${randomSlug}`)
  return {
    id: uuid4(),
    isList: Boolean(isList),
    title: pageTitle,
    slug: pageSlug,
    visible: true,
    enabled: true,
    icon: MATERIAL_ICONS[Math.floor(Math.random() * MATERIAL_ICONS.length)]?.value ?? 'home',
    bgColor: 'primary-100',
    tbType: TopBarType.AppBar,
    tbPageTitle: true,
    tbLeadingElementLogOut: tableId
      ? {
          id: uuid4(),
          flag: VariableSelectFeatureFlag.Icon,
          value: 'arrow_back',
          actions: {
            type: IActionType.GoBack,
            settings: {},
          },
        }
      : undefined,
    tbLeadingElementLogIn: tableId
      ? {
          id: uuid4(),
          flag: VariableSelectFeatureFlag.Icon,
          value: 'arrow_back',
          actions: {
            type: IActionType.GoBack,
            settings: {},
          },
        }
      : undefined,
    tbTrailingElements: [],
    bbType: BottomBarType.Global,
    bbContextualWithFab: false,
    bbContextualMenu: undefined,
    bbContextualFabIcon: 'edit',
    bbContextualFabActions: undefined,
    seoTitle: '',
    seoKeywords: '',
    seoDescription: '',
    badgeMetadata: undefined,
    elements: [],
    elementSettings: {},
    elementSettingsTranslation: {},
    visibility: {
      isAny: false,
      conditions: [],
    },
    actions: {
      onLoad: [],
    },
    tableId,
    type: undefined,
    private: isPrivate,
    alignment: 'center',
  }
}

export function genNewPageAlt(title: string, tableId?: string, isList?: boolean, nature?: PageNature): BuilderPage {
  return {
    id: uuid4(),
    isList: Boolean(isList),
    title,
    visible: true,
    enabled: true,
    icon: MATERIAL_ICONS[Math.floor(Math.random() * MATERIAL_ICONS.length)]?.value ?? 'home',
    bgColor: 'primary-100',
    tbType: TopBarType.AppBar,
    tbPageTitle: true,
    tbLeadingElementLogOut: tableId
      ? {
          id: uuid4(),
          flag: VariableSelectFeatureFlag.Icon,
          value: 'arrow_back',
          actions: {
            type: IActionType.GoBack,
            settings: {},
          },
        }
      : undefined,
    tbLeadingElementLogIn: tableId
      ? {
          id: uuid4(),
          flag: VariableSelectFeatureFlag.Icon,
          value: 'arrow_back',
          actions: {
            type: IActionType.GoBack,
            settings: {},
          },
        }
      : undefined,
    tbTrailingElements: [],
    bbType: BottomBarType.Global,
    bbContextualWithFab: false,
    bbContextualMenu: undefined,
    bbContextualFabIcon: 'edit',
    bbContextualFabActions: undefined,
    seoTitle: '',
    seoKeywords: '',
    seoDescription: '',
    badgeMetadata: undefined,
    elements: [],
    elementSettings: {},
    elementSettingsTranslation: {},
    visibility: {
      isAny: false,
      conditions: [],
    },
    actions: {
      onLoad: [],
    },
    tableId,
    type: undefined,
    nature,
    alignment: 'center',
  }
}

function createElementSettings(
  component: BuilderComponent,
  context: {
    menus: BuilderMenu[]
    tables: TableAdapter[]
    flattened: any[]
  },
  tableId?: string
) {
  return parseCompSettings(component, context, tableId)
}

/**
 * Generate details page
 */
export function genDetailsPage(
  table: TableAdapter,
  tables: TableAdapter[],
  menus: BuilderMenu[] = [],
  components: BuilderComponentGroup[] = DEFAULT_COMPONENTS_GROUPS
) {
  const newPage = genNewPageAlt(`detail-page-${table.name}`, table.id, true, PageNature.Standard)

  const groupComp = components.find((c) => c.id === 'table')
  const dlComp = groupComp?.components.find((c) => c.compId === 'data-list')
  const mediaComp = components.find((c) => c.id === 'media')
  const imgComp = mediaComp?.components.find((c) => c.compId === 'image')

  if (groupComp && dlComp) {
    const isImageAvailable = table.columns.some((column) => column.type === 'image')

    const elements = []
    const elementSettings: Record<string, any> = {}

    if (isImageAvailable && imgComp && mediaComp) {
      const imageElement = createBuilderElement(imgComp, mediaComp.id)
      const imageDefaultSettings = createElementSettings(imgComp, { menus, tables, flattened: [] }, table.id)

      elements.push(imageElement)
      elementSettings[imageElement.id] = { ...imageDefaultSettings }
    }

    const dataListElement = createBuilderElement(dlComp, groupComp.id)
    const dataListDefaultSettings = createElementSettings(dlComp, {
      menus,
      tables,
      flattened: [],
    })

    elements.push(dataListElement)
    elementSettings[dataListElement.id] = {
      ...dataListDefaultSettings,
      [ComponentSettingKey.DataListData]: {
        table: table.id,
        isDetailPageTable: true,
        columns: table.columns
          .filter((c) =>
            [
              ColumnTypes.Text,
              ColumnTypes.Number,
              ColumnTypes.Boolean,
              ColumnTypes.Icon,
              ColumnTypes.DateTime,
            ].includes(c.type)
          )
          .map((c) => ({
            id: uuid4(),
            label: c.name,
            col: c.id,
            userSpecific: false,
            icon: undefined,
          })) as TableColumnListColumnValue[],
      } as TableColumnListValue,
    }

    // * this logic is moved to app-builder.ts
    // * because the back icon should be available in both detail and detail form page
    //
    // newPage.tbLeadingElementLogOut = {
    //   id: uuid4(),
    //   flag: VariableSelectFeatureFlag.Icon,
    //   value: 'arrow_back',
    //   actions: {
    //     type: IActionType.GoBack,
    //     settings: {},
    //   },
    // }

    // newPage.tbLeadingElementLogIn = {
    //   id: uuid4(),
    //   flag: VariableSelectFeatureFlag.Icon,
    //   value: 'arrow_back',
    //   actions: {
    //     type: IActionType.GoBack,
    //     settings: {},
    //   },
    // }

    newPage.elements = elements
    newPage.elementSettings = elementSettings
  }

  return newPage
}

/**
 * Generate a form details page
 */
export function genFormDetailsPage(table: TableAdapter, tables: TableAdapter[]) {
  const formDetailsPage = genNewPageAlt(`form-detail-${table.name}`, table.id, true, PageNature.Form)

  const formElComp = ContainerComponents.components.find((c) => c.compId === 'form')

  if (!formElComp) return formDetailsPage

  // generate form parent
  let formEl = createBuilderElement(formElComp, ContainerComponents.id)
  let formSetings = parseCompSettings(
    formElComp,
    {
      menus: [],
      tables,
      flattened: [],
    },
    table.id
  )

  // generate form fields
  const { children, childElSettings } = generateAutoFormFields(tables, formEl.id, formSetings, table.id, 'form-update')

  // Append form fields to form element
  formEl.children = children

  // Append form to page
  formDetailsPage.elements.push(formEl)

  // Append all settings to page
  formDetailsPage.elementSettings = {
    [formEl.id]: formSetings,
    ...childElSettings,
  }

  return formDetailsPage
}

/**
 * Generate form add item details page
 */
export function genFormAddDetailsPage(table: TableAdapter, tables: TableAdapter[]) {
  const addItemDetailsPage = genNewPageAlt(`form-add-${table.name}`, table.id, true, PageNature.Form)

  const formElComp = ContainerComponents.components.find((c) => c.compId === 'form')

  if (!formElComp) return addItemDetailsPage

  // generate form parent
  let formEl = createBuilderElement(formElComp, ContainerComponents.id)
  let formSetings = parseCompSettings(
    formElComp,
    {
      menus: [],
      tables,
      flattened: [],
    },
    table.id
  )
  formSetings[ComponentSettingKey.FormDataCompTitle] = [
    {
      type: 'paragraph',
      children: [
        {
          text: 'Add New Item',
        },
      ],
    },
  ]

  // generate form fields
  const { children, childElSettings } = generateAutoFormFields(tables, formEl.id, formSetings, table.id, 'form-add')

  // Append form fields to form element
  formEl.children = children

  // Append form to page
  addItemDetailsPage.elements.push(formEl)

  // Append all settings to page
  addItemDetailsPage.elementSettings = {
    [formEl.id]: formSetings,
    ...childElSettings,
  }

  return addItemDetailsPage
}

// ============================== Utils ==============================

/**
 * Find a component in a body array by ID.
 */
export const findComponentInBody = (bodyArray: IBody[], componentId: string): IBody | null => {
  for (let component of bodyArray) {
    if (component._id === componentId) {
      return component
    }

    // Recursively search in nested _Body arrays
    if (component._Body && component._Body.length > 0) {
      const foundInNestedBody = findComponentInBody(component._Body, componentId)
      if (foundInNestedBody) {
        return foundInNestedBody
      }
    }
  }
  return null
}

export const filterComponentInBody = (bodyArray: IBody[], cbFilter: (component: IBody) => boolean): IBody[] => {
  let result = new Set<IBody>()

  bodyArray.forEach((component) => {
    if (cbFilter(component)) {
      result.add(component)
    }

    // Recursively search in nested _Body arrays
    if (component._Body && component._Body.length > 0) {
      const foundInNestedBody = filterComponentInBody(component._Body, cbFilter)
      if (foundInNestedBody) {
        foundInNestedBody.forEach((c) => result.add(c))
      }
    }
  })

  return Array.from(result)
}

/**
 * Clone a component and generate new IDs for the clone and its nested components.
 */
export const cloneWithNewIds = (
  component: IBody,
  originalSettings: IBodySettings,
  clonedSettings: IBodySettings
): IBody => {
  const clonedComponent = cloneDeep(component)

  // assign new id
  const newId = uuid4()
  const oldId = component._id
  clonedComponent._id = newId

  // Clone the settings for this component
  if (originalSettings[oldId]) {
    clonedSettings[newId] = cloneDeep(originalSettings[oldId])
  }

  // Recursively clone any nested components in the _Body array
  if (clonedComponent._Body && clonedComponent._Body.length > 0) {
    clonedComponent._Body = clonedComponent._Body.map((childComponent) =>
      cloneWithNewIds(childComponent, originalSettings, clonedSettings)
    )
  }

  return clonedComponent
}

/**
 * Insert the cloned component into the _Body array after the original component.
 */
export const cloneInBody = (bodyArray: IBody[], componentId: string, clonedComponent: IBody): IBody[] => {
  return bodyArray.flatMap((component) => {
    if (component._id === componentId) {
      // Insert cloned component after the original one
      return [component, clonedComponent]
    }

    // Recursively check nested components in case the match is deeper
    if (component._Body && component._Body.length > 0) {
      component._Body = cloneInBody(component._Body, componentId, clonedComponent)
    }

    return [component]
  })
}

export const appendToBody = (bodyArray: IBody[], clonedComponent: IBody): IBody[] => {
  return [...bodyArray, clonedComponent]
}

// export const renameComponentInBody = (bodyArray: IBody[], componentId: string, newName: string): IBody | null => {
//   for (let component of bodyArray) {
//     if (component._id === componentId) {
//       component._Title = newName
//       return component
//     }

//     if (component._Body?.length > 0) {
//       const foundComponent = renameComponentInBody(component._Body, componentId, newName)
//       if (foundComponent) return foundComponent
//     }
//   }
//   return null
// }

export const updateNestedBody = (bodyArray: IBody[], updatedComponent: IBody): IBody[] => {
  return bodyArray.map((component) => {
    // If this is the component we want to update
    if (component._id === updatedComponent._id) {
      return updatedComponent
    }

    // If this component has children, recursively search and update
    if (component._Body && component._Body.length > 0) {
      return {
        ...component,
        _Body: updateNestedBody(component._Body, updatedComponent),
      }
    }

    // Return unchanged component if no match
    return component
  })
}

export const checkIsSubmitButton = (comp: IBody, bodySettings: Record<string, any> = {}) => {
  // check if the button is a submit button
  const isButton = comp._ComponentID === 'button'

  if (!isButton) return false

  const isSubmit = comp._IsSubmit

  // check if the button has submit action in settings
  const btnSettings = bodySettings[comp._id]

  if (!btnSettings) return false

  const hasFormAction = (btnSettings[ComponentSettingKey.ButtonActionClick] as IActionRepeaterValue[]).some((action) =>
    [IActionType.FormAddRow, IActionType.FormUpdateRow].includes(action.type)
  )

  return isSubmit || (isButton && hasFormAction)
}

// initial format: (form-add | form-detail)-<Table Name>
// example: form-add-users -> Form Add (Users)
// example: form-detail-users -> Form Update (Users)
export function formatFormDetailScreenTitle(pageTitle?: string) {
  if (!pageTitle) return ''

  // // extract the table name from the page title
  // const tableName = pageTitle.replace('form-add-', '').replace('form-detail-', '')
  // const prefix = pageTitle.startsWith('form-add-') ? 'Form Add' : 'Form Update'

  // // convert the table name to title case
  // const titleCaseTableName = tableName.replace('-', ' ')

  // return `${prefix} (${titleCaseTableName})`

  return pageTitle.startsWith('form-add-') ? 'Add New Item' : 'Update Item'
}

// initial title: form-add-<Table Name>
// fixed slug: <table-name>-form-add (lowercase)
export function formatFormDetailScreenSlug(title: string) {
  return title.startsWith('form-add-')
    ? title.replace('form-add-', '').replace('-', ' ').toLowerCase() + '-form-add'
    : title.startsWith('form-detail-')
      ? title.replace('form-detail-', '').replace('-', ' ').toLowerCase() + '-form'
      : title
}
